/**
 * Rapid X - Executive Command Centre
 * Real-time security operations dashboard with live map and breadcrumb tracking.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useWebSocket } from '../lib/useWebSocket';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import KpiDrilldownSheet from '../components/KpiDrilldownSheet';
import {
  AlertTriangle, Shield, Users, MapPin, Clock, Activity,
  RefreshCw, Loader2, Building2, Navigation, ChevronRight,
  AlertCircle, CheckCircle2, Radio, Eye, Route, Play, Pause,
  Battery, Footprints, Trash2
} from 'lucide-react';

// Command Centre Map (Trakpro-style)
import CommandCentreMap, { MarkerStyles } from '../components/CommandCentreMap';
import OfficerAvatar from '../components/common/OfficerAvatar';

// Location tracking components (for other panels)
import { 
  ReplayControls, 
  OfficerTrackingDetails,
} from '../components/LocationTracking';

const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#3b82f6',
};


function EmergencyAuditTrail({ alert }) {
  const history = alert?.workflow_history || [];

  if (!alert?.alert_id || !history.length) return null;

  const labels = {
    panic_triggered: "PANIC TRIGGERED",
    control_room_acknowledged: "ACKNOWLEDGED",
    response_commenced: "RESPONSE COMMENCED",
    emergency_resolved: "RESOLVED",
  };

  const formatAuditTime = (value) => {
    if (!value) return "--:--:--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--:--:--";
    return date.toLocaleTimeString("en-AU", {
      timeZone: "Australia/Sydney",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  return (
    <div className="rounded-xl border border-red-500/30 bg-black/40 p-4">
      <div className="text-xs font-black uppercase tracking-[0.2em] text-red-300 mb-3">
        Emergency Audit Trail
      </div>

      <div className="space-y-3">
        {history.map((item, index) => (
          <div
            key={`${item.event || "event"}-${item.timestamp || index}`}
            className="grid grid-cols-[76px_1fr] gap-3 text-sm"
          >
            <div className="font-mono text-zinc-400">
              {formatAuditTime(item.timestamp)}
            </div>

            <div>
              <div className="font-bold text-white">
                {labels[item.event] || (item.status || item.event || "EVENT").toUpperCase()}
              </div>

              {item.actor_name && (
                <div className="text-zinc-300">
                  {item.actor_name}
                </div>
              )}

              {item.note && (
                <div className="text-zinc-400 mt-1 whitespace-pre-wrap">
                  {item.note}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExecutivePage() {
  const { api, isAdmin } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [incidentsFeed, setIncidentsFeed] = useState([]);
  const [attendanceFeed, setAttendanceFeed] = useState([]);
  const [markers, setMarkers] = useState({ incidents: [], officers: [], sites: [] });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markerDetails, setMarkerDetails] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Tracking state
  const [selectedOfficerTracking, setSelectedOfficerTracking] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [siteVisits, setSiteVisits] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);

  // Active panic currently driving Command Centre map focus.
  const [activePanicFocus, setActivePanicFocus] = useState(null);
  
  // Feed management state
  const [showClearIncidentsDialog, setShowClearIncidentsDialog] = useState(false);
  const [showClearAttendanceDialog, setShowClearAttendanceDialog] = useState(false);
  const [clearingIncidents, setClearingIncidents] = useState(false);
  const [clearingAttendance, setClearingAttendance] = useState(false);
  const [activePatrols, setActivePatrols] = useState([]);
  const [drilldown, setDrilldown] = useState({ open: false, kpiType: null, title: null, value: null });

  // WebSocket handler
  const handleWsMessage = useCallback((message) => {
    if (message.type === 'executive_update') {
      // Handle executive updates
      refreshData();
    } else if (message.type === 'attendance_update') {
      // Refresh attendance feed
      loadAttendanceFeed();
    } else if (message.type === 'incident_update') {
      // Refresh incidents feed
      loadIncidentsFeed();
    } else if (message.type === 'panic_focus') {
      const latitude = Number(message.latitude);
      const longitude = Number(message.longitude);

      setActivePanicFocus({
        alert_id: message.alert_id,
        officer_id: message.officer_id,
        officer_name: message.officer_name || 'Officer',
        shift_id: message.shift_id,
        site_name: message.site_name,
        latitude: Number.isFinite(latitude) ? latitude : null,
        longitude: Number.isFinite(longitude) ? longitude : null,
        accuracy: message.accuracy,
        status: message.status || 'active',
        timestamp: message.timestamp,
      });

      toast.error(
        `PANIC ALERT: ${message.officer_name || 'Officer'} needs urgent assistance`,
        {
          duration: 15000,
          icon: <AlertTriangle className="w-4 h-4 text-red-500" />
        }
      );

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        setMarkers(prev => ({
          ...prev,
          officers: (prev.officers || []).map(officer =>
            String(officer.id) === String(message.officer_id)
              ? {
                  ...officer,
                  lat: latitude,
                  lng: longitude,
                  panic_active: true,
                  panic_alert_id: message.alert_id,
                  last_update: message.timestamp,
                }
              : officer
          )
        }));
      }
    } else if (
      message.type === 'dispatch_event' &&
      message.category === 'panic' &&
      (
        message.payload?.event === 'panic_acknowledged' ||
        message.payload?.event === 'panic_responding'
      )
    ) {
      const payload = message.payload || {};
      const alertId = payload.alert_id;

      setActivePanicFocus(current => {
        if (!current || String(current.alert_id) !== String(alertId)) {
          return current;
        }

        return {
          ...current,
          ...payload,
          status:
            payload.event === 'panic_responding'
              ? 'responding'
              : 'acknowledged',
        };
      });

    } else if (
      message.type === 'dispatch_event' &&
      message.category === 'panic' &&
      message.payload?.event === 'panic_resolved'
    ) {
      const resolvedAlertId = message.payload?.alert_id;

      setActivePanicFocus(current =>
        current &&
        String(current.alert_id) === String(resolvedAlertId)
          ? null
          : current
      );

      setMarkers(prev => ({
        ...prev,
        officers: (prev.officers || []).map(officer =>
          String(officer.panic_alert_id) === String(resolvedAlertId)
            ? {
                ...officer,
                panic_active: false,
                panic_alert_id: null,
              }
            : officer
        )
      }));
    } else if (message.type === 'location_update' || message.type === 'location_ping') {
      // Live location update from officer
      handleLocationUpdate(message);
    } else if (message.type === 'site_visit') {
      // Site arrival/departure event
      handleSiteVisitEvent(message);
    } else if (message.type === 'dashboard_update') {
      // Update specific KPI
      if (message.metric === 'on_duty_count' && kpis) {
        setKpis(prev => prev ? { ...prev, on_duty_officers: message.value } : prev);
      }
    }
    setLastUpdate(new Date());
  }, [kpis]);

  const { connected, reconnect } = useWebSocket(handleWsMessage);
  
  useEffect(() => {
    setWsConnected(connected);
  }, [connected]);

  // Initial load
  useEffect(() => {
    loadAllData();
    
    // Auto-refresh every 10 seconds — ALL live data for operational awareness
    const interval = setInterval(() => {
      loadKpis();
      loadMapMarkers();
      loadActivePatrols();
      loadActivePanicFocus();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadActivePanicFocus = async () => {
    try {
      const response = await api.get('/panic/active');

      const alerts =
        response?.data?.alerts ||
        response?.data ||
        [];

      const activeAlerts = Array.isArray(alerts)
        ? alerts
        : [];

      if (!activeAlerts.length) {
        setActivePanicFocus(null);
        return;
      }

      const alert = activeAlerts[0];

      let panicAudit = {};
      try {
        const auditResponse = await api.get(`/panic/${alert.alert_id}/audit`);
        panicAudit = auditResponse?.data || {};
      } catch (auditError) {
        console.error('Failed to load panic audit trail', auditError);
      }

      const latitude = Number(alert.latitude);
      const longitude = Number(alert.longitude);

      setActivePanicFocus({
        ...alert,
        ...panicAudit,
        alert_id: alert.alert_id,
        officer_id: alert.officer_id,
        officer_name: alert.officer_name || 'Officer',
        shift_id: alert.shift_id,
        site_name: alert.site_name,
        latitude: Number.isFinite(latitude) ? latitude : null,
        longitude: Number.isFinite(longitude) ? longitude : null,
        accuracy: alert.accuracy,
        status: alert.status || 'active',
        timestamp:
          alert.timestamp ||
          alert.created_at ||
          alert.triggered_at,
      });

      setMarkers(prev => ({
        ...prev,
        officers: (prev.officers || []).map(officer =>
          String(officer.id) === String(alert.officer_id)
            ? {
                ...officer,
                ...(Number.isFinite(latitude) ? { lat: latitude } : {}),
                ...(Number.isFinite(longitude) ? { lng: longitude } : {}),
                panic_active: true,
                panic_alert_id: alert.alert_id,
              }
            : officer
        )
      }));

    } catch (error) {
      console.error('Failed to restore active panic focus', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadKpis(),
      loadIncidentsFeed(),
      loadAttendanceFeed(),
      loadMapMarkers(),
      loadActivePatrols(),
      loadActivePanicFocus(),
    ]);
    setLoading(false);
  };

  const refreshData = async () => {
    await Promise.all([
      loadKpis(),
      loadIncidentsFeed(),
      loadAttendanceFeed(),
      loadMapMarkers(),
      loadActivePatrols(),
      loadActivePanicFocus(),
    ]);
    setLastUpdate(new Date());
  };

  const loadKpis = async () => {
    try {
      const response = await api.get('/executive/kpis');
      setKpis(response.data);
    } catch (error) {
      console.error('Error loading KPIs:', error);
    }
  };

  const loadIncidentsFeed = async () => {
    try {
      const response = await api.get('/executive/incidents-feed?limit=20');
      setIncidentsFeed(response.data.incidents || []);
    } catch (error) {
      console.error('Error loading incidents feed:', error);
    }
  };

  const loadAttendanceFeed = async () => {
    try {
      const response = await api.get('/executive/attendance-feed?limit=20');
      setAttendanceFeed(response.data.activities || []);
    } catch (error) {
      console.error('Error loading attendance feed:', error);
    }
  };

  const loadActivePatrols = async () => {
    try {
      const response = await api.get('/executive/active-patrols');
      setActivePatrols(response.data.patrols || []);
    } catch (error) {
      console.error('Error loading active patrols:', error);
    }
  };

  const loadMapMarkers = async () => {
    try {
      const response = await api.get('/executive/map/markers');
      setMarkers(response.data);
      // Map centering is handled by CommandCentreMap component internally
    } catch (error) {
      console.error('Error loading map markers:', error);
    }
  };

  const loadMarkerDetails = async (type, id) => {
    try {
      const endpoint = type === 'incident' 
        ? `/executive/map/incident/${id}`
        : `/executive/map/officer/${id}`;
      const response = await api.get(endpoint);
      setMarkerDetails(response.data);
    } catch (error) {
      console.error('Error loading marker details:', error);
      toast.error('Failed to load details');
    }
  };

  // ========== TRACKING FUNCTIONS ==========
  
  // Load tracking data for selected officer
  const loadOfficerTracking = async (officerId, shiftId) => {
    if (!shiftId) return;

    setTrackingLoading(true);
    try {
      // Load current tracking status
      const statusRes = await api.get(`/tracking/officer/${officerId}/current`);
      setSelectedOfficerTracking(statusRes.data);

      // P2: Unified forensic timeline
      const timelineRes = await api.get(`/tracking/timeline/${shiftId}`);
      const timelineEvents = timelineRes.data.events || [];

      const gpsEvents = timelineEvents
        .filter((event) => event.type === 'gps' && event.latitude && event.longitude)
        .map((event) => ({
          latitude: event.latitude,
          longitude: event.longitude,
          timestamp: event.timestamp,
          accuracy: event.payload?.accuracy,
          speed: event.payload?.speed,
          heading: event.payload?.heading,
          battery_level: event.payload?.battery_level,
          movement_state: event.payload?.movement_state,
        }));

      const visitEvents = timelineEvents
        .filter((event) => event.type === 'site_arrival' || event.type === 'site_departure')
        .map((event) => ({
          id: `${event.type}-${event.timestamp}`,
          site_id: event.payload?.site_id,
          site_name: event.description || event.title,
          arrival_time: event.type === 'site_arrival' ? event.timestamp : null,
          departure_time: event.type === 'site_departure' ? event.timestamp : null,
          latitude: event.latitude,
          longitude: event.longitude,
          event_type: event.type,
        }));

      setLocationHistory(gpsEvents);
      setSiteVisits(visitEvents);

      // Keep existing replay behaviour; CommandCentreMap owns its local replay state.
      setReplayIndex(gpsEvents.length - 1 || 0);
    } catch (error) {
      console.error('Error loading officer tracking:', error);
      // Fallback to old endpoints if timeline endpoint is unavailable
      try {
        const historyRes = await api.get(`/tracking/history/${shiftId}`);
        setLocationHistory(historyRes.data.locations || []);

        const visitsRes = await api.get(`/tracking/site-visits/${shiftId}`);
        setSiteVisits(visitsRes.data.visits || []);
      } catch (fallbackError) {
        console.error('Fallback tracking load failed:', fallbackError);
      }
    } finally {
      setTrackingLoading(false);
    }
  };
  
  // Handle live location update from WebSocket
  const handleLocationUpdate = useCallback((message) => {
    // Update officer marker position
    setMarkers(prev => ({
      ...prev,
      officers: prev.officers.map(officer => 
        officer.id === message.officer_id
          ? {
              ...officer,
              lat: message.latitude,
              lng: message.longitude,
              battery_level: message.battery_level,
              movement_state: message.movement_state,
              last_update: message.timestamp,
            }
          : officer
      )
    }));
    
    // Keep the active panic focus aligned with live officer GPS updates.
    setActivePanicFocus(current => {
      if (
        !current ||
        String(current.officer_id) !== String(message.officer_id)
      ) {
        return current;
      }

      return {
        ...current,
        latitude: message.latitude,
        longitude: message.longitude,
        timestamp: message.timestamp,
      };
    });

    // If this officer is selected, add to location history
    if (selectedOfficerTracking?.officer_id === message.officer_id) {
      setLocationHistory(prev => [...prev, {
        latitude: message.latitude,
        longitude: message.longitude,
        timestamp: message.timestamp,
        speed: message.speed,
        battery_level: message.battery_level,
        movement_state: message.movement_state,
      }]);
    }
  }, [selectedOfficerTracking]);
  
  // Handle site visit event from WebSocket
  const handleSiteVisitEvent = useCallback((message) => {
    if (message.event === 'arrival') {
      toast.success(`${message.officer_name || 'Officer'} arrived at ${message.site_name}`, {
        icon: <MapPin className="w-4 h-4 text-emerald-500" />
      });
    } else if (message.event === 'departure') {
      toast.info(`${message.officer_name || 'Officer'} left ${message.site_name}`, {
        icon: <MapPin className="w-4 h-4 text-zinc-500" />
      });
    }
    
    // Refresh site visits if this officer is selected
    if (selectedOfficerTracking?.shift_id === message.shift_id) {
      loadOfficerTracking(message.officer_id, message.shift_id);
    }
  }, [selectedOfficerTracking]);

  const formatTime = (isoString) => {
    if (!isoString) return '--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-AU', {
      timeZone: 'Australia/Sydney',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '--';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Clear all incidents from feed
  const handleClearAllIncidents = async () => {
    setClearingIncidents(true);
    try {
      const response = await api.post('/feeds/incidents/clear', {
        older_than_days: 0, // All incidents
        archive: true // Archive, not delete
      });
      toast.success(response.data?.message || 'Incidents cleared successfully');
      setShowClearIncidentsDialog(false);
      refreshData(); // Refresh dashboard
    } catch (error) {
      console.error('Clear incidents error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear incidents');
    } finally {
      setClearingIncidents(false);
    }
  };

  // Clear all attendance from feed
  const handleClearAllAttendance = async () => {
    setClearingAttendance(true);
    try {
      const response = await api.post('/feeds/attendance/clear', {
        older_than_days: 0, // All attendance records
        archive: true
      });
      toast.success(response.data?.message || 'Attendance records cleared');
      setShowClearAttendanceDialog(false);
      refreshData(); // Refresh dashboard
    } catch (error) {
      console.error('Clear attendance error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear attendance');
    } finally {
      setClearingAttendance(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading Command Centre...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Header */}
      <div className="border-b border-[#3f3f46] bg-[#18181b]">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-rajdhani font-bold tracking-wide">
              EXECUTIVE COMMAND CENTRE
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Real-time Security Operations Overview
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-zinc-400">{wsConnected ? 'Live' : 'Disconnected'}</span>
            </div>
            {lastUpdate && (
              <span className="text-xs text-zinc-500">
                Updated: {lastUpdate.toLocaleTimeString('en-AU', {
                  timeZone: 'Australia/Sydney',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={refreshData}
              className="border-zinc-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {activePanicFocus && (
        <div className="px-4 pb-4">
          <EmergencyAuditTrail alert={activePanicFocus} />
        </div>
      )}

      {/* Main Content */}
      {/* Operations Map - Trakpro Style Command Centre */}
      <div id="command-centre-map" className="p-4 pt-0">
        <MarkerStyles />
        <CommandCentreMap
          officers={markers.officers || []}
          sites={markers.sites || []}
          incidents={markers.incidents || []}
          locationHistory={locationHistory}
          siteVisits={siteVisits}
          panicFocus={activePanicFocus}
          onOfficerSelect={(officer) => {
            setSelectedMarker({ type: 'officer', id: officer.id, shift_id: officer.shift_id });

            // Officer identity/map details are already supplied by
            // /executive/map/markers. Do not call the obsolete
            // /executive/map/officer/{id} endpoint.
            setMarkerDetails(officer);

            if (officer.shift_id) {
              loadOfficerTracking(officer.id, officer.shift_id);
            }
          }}
          onRefresh={refreshData}
          isLoading={loading}
        />
      </div>

      <div className="grid grid-cols-12 gap-4 p-4">
        {/* Left: Incident Feed (5 cols) */}
        <div className="col-span-5">
          <Card className="bg-[#18181b] border-[#3f3f46] h-[400px] flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-rajdhani flex items-center gap-2">
                  <Radio className="w-4 h-4 text-red-400 animate-pulse" />
                  LIVE INCIDENT FEED
                  {incidentsFeed.length > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 ml-2">{incidentsFeed.length}</Badge>
                  )}
                </CardTitle>
                {isAdmin && incidentsFeed.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowClearIncidentsDialog(true)}
                    className="text-zinc-500 hover:text-red-400 h-7 px-2"
                    data-testid="clear-incidents-feed-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {incidentsFeed.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No active incidents</p>
                ) : (
                  incidentsFeed.map((incident) => (
                    <div
                      key={incident.id}
                      className={`p-3 rounded border border-[#3f3f46] hover:border-zinc-500 cursor-pointer transition-all ${
                        incident.severity === 'critical' ? 'bg-red-500/10 border-red-500/50 animate-pulse' : 'bg-[#09090b]'
                      }`}
                      onClick={() => {
                        setSelectedMarker({ type: 'incident', id: incident.id });
                        loadMarkerDetails('incident', incident.id);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              className="text-[10px]"
                              style={{ backgroundColor: `${SEVERITY_COLORS[incident.severity]}20`, color: SEVERITY_COLORS[incident.severity] }}
                            >
                              {incident.severity?.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-zinc-500 font-mono">{incident.ir_number}</span>
                          </div>
                          <div className="text-sm text-white">{incident.title || 'Incident'}</div>
                          <div className="text-xs text-zinc-500 mt-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {incident.site_name}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-zinc-500">{formatTimeAgo(incident.created_at)}</div>
                          <Badge className="text-[10px] mt-1 bg-zinc-700">{incident.status}</Badge>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Attendance Feed (3 cols) + KPI Summary (4 cols) */}
        <div className="col-span-3">
          <Card className="bg-[#18181b] border-[#3f3f46] h-[400px] flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-rajdhani flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-400" />
                  ATTENDANCE ACTIVITY
                  {attendanceFeed.length > 0 && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 ml-2">{attendanceFeed.length}</Badge>
                  )}
                </CardTitle>
                {isAdmin && attendanceFeed.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowClearAttendanceDialog(true)}
                    className="text-zinc-500 hover:text-amber-400 h-7 px-2"
                    data-testid="clear-attendance-feed-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {attendanceFeed.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No recent activity</p>
                ) : (
                  attendanceFeed.map((activity) => (
                    <div
                      key={activity.id}
                      className={`p-2 rounded border border-[#3f3f46] text-xs ${
                        activity.has_anomaly ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-[#09090b]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {activity.action === 'clock_in' ? (
                            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                          ) : (
                            <div className="w-2 h-2 bg-zinc-500 rounded-full" />
                          )}
                          <span className="text-white">{activity.user_name}</span>
                          {activity.has_anomaly && (
                            <AlertTriangle className="w-3 h-3 text-yellow-500" />
                          )}
                        </div>
                        <span className="text-zinc-500">{formatTime(activity.timestamp)}</span>
                      </div>
                      <div className="text-zinc-500 mt-1 pl-4">
                        {activity.action === 'clock_in' ? 'IN' : 'OUT'} @ {activity.site_name}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Summary */}
        <div className="col-span-4 space-y-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-rajdhani">INCIDENTS BY SEVERITY (TODAY)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2">
                {['critical', 'high', 'medium', 'low'].map((sev) => (
                  <div key={sev} className="text-center p-2 rounded bg-[#09090b]">
                    <div
                      className="text-2xl font-bold"
                      style={{ color: SEVERITY_COLORS[sev] }}
                    >
                      {kpis?.incidents_by_severity?.[sev] || 0}
                    </div>
                    <div className="text-[10px] text-zinc-500 uppercase">{sev}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-rajdhani flex items-center gap-2">
                <Navigation className="w-4 h-4 text-blue-400" />
                ACTIVE PATROLS
                {activePatrols.length > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 ml-1">{activePatrols.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[120px] overflow-y-auto">
                {activePatrols.length === 0 ? (
                  <p className="text-zinc-500 text-center text-xs py-3">No active patrols</p>
                ) : (
                  activePatrols.map((patrol) => (
                    <div key={patrol.id} className="p-2 rounded bg-[#09090b] border border-[#3f3f46]" data-testid={`active-patrol-${patrol.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-white truncate">{patrol.officer_name}</span>
                        <Badge className={`text-[9px] px-1 py-0 ${
                          patrol.time_status === 'exception' ? 'bg-red-500/20 text-red-400' :
                          patrol.time_status === 'delayed' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {patrol.time_status}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-zinc-400 mb-1">{patrol.plan_name}</div>
                      <div className="flex items-center gap-2">
                        <Progress value={patrol.completion_percentage} className="h-1.5 flex-1" />
                        <span className="text-[10px] text-zinc-500">{patrol.sites_completed}/{patrol.sites_total}</span>
                      </div>
                      {patrol.current_site && (
                        <div className="text-[10px] text-blue-400 mt-1 flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5" /> {patrol.current_site}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-rajdhani">OFFICERS BY SITE</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[120px] overflow-y-auto">
                {kpis?.on_duty_by_site?.length === 0 ? (
                  <p className="text-zinc-500 text-center text-xs">No officers on duty</p>
                ) : (
                  kpis?.on_duty_by_site?.map((site) => (
                    <div key={site.site_id} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 truncate">{site.site_name}</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400">{site.officers}</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>


      {/* Global Status Bar */}
      <div className="grid grid-cols-6 gap-4 p-4 border-b border-[#3f3f46]">
        <StatusCard
          icon={<Shield className="w-6 h-6" />}
          title="Active Incidents"
          value={kpis?.active_incidents || 0}
          badge={kpis?.critical_incidents > 0 ? `${kpis.critical_incidents} Critical` : null}
          badgeColor="red"
          trend={kpis?.incidents_by_severity}
          kpiType="active_incidents"
          onClick={() => setDrilldown({ open: true, kpiType: 'active_incidents', title: 'Active Incidents', value: kpis?.active_incidents || 0 })}
        />
        <StatusCard
          icon={<Users className="w-6 h-6" />}
          title="On-Duty Officers"
          value={kpis?.on_duty_officers || 0}
          subtitle="Currently clocked in"
          color="emerald"
          kpiType="on_duty_officers"
          onClick={() => setDrilldown({ open: true, kpiType: 'on_duty_officers', title: 'On-Duty Officers', value: kpis?.on_duty_officers || 0 })}
        />
        <StatusCard
          icon={<Building2 className="w-6 h-6" />}
          title="Sites Covered"
          value={`${kpis?.sites_covered || 0} / ${kpis?.total_sites || 0}`}
          badge={kpis?.sites_unstaffed > 0 ? `${kpis.sites_unstaffed} Unstaffed` : null}
          badgeColor="yellow"
        />
        <StatusCard
          icon={<Navigation className="w-6 h-6" />}
          title="Active Patrols"
          value={kpis?.patrol_execution_stats?.active_patrols || 0}
          badge={kpis?.patrol_execution_stats?.high_priority_reviews > 0 ? `${kpis.patrol_execution_stats.high_priority_reviews} Alerts` : null}
          badgeColor="red"
          subtitle={`${kpis?.patrol_execution_stats?.completion_rate || 100}% success rate`}
          color="blue"
          kpiType="active_patrols"
          onClick={() => setDrilldown({ open: true, kpiType: 'active_patrols', title: 'Active Patrols', value: kpis?.patrol_execution_stats?.active_patrols || 0 })}
        />
        <StatusCard
          icon={<AlertTriangle className="w-6 h-6" />}
          title="Attendance Anomalies"
          value={kpis?.attendance_anomalies_today || 0}
          subtitle="Today"
          color={kpis?.attendance_anomalies_today > 0 ? 'yellow' : 'zinc'}
          kpiType="attendance_anomalies"
          onClick={() => setDrilldown({ open: true, kpiType: 'attendance_anomalies', title: 'Attendance Anomalies', value: kpis?.attendance_anomalies_today || 0 })}
        />
        <StatusCard
          icon={<CheckCircle2 className="w-6 h-6" />}
          title="Fleet Compliance"
          value={`${kpis?.compliance_rate || 100}%`}
          subtitle={`${kpis?.fleet_compliance?.total_officers || 0} officers`}
          color={kpis?.compliance_rate < 90 ? 'red' : 'emerald'}
        />
      </div>


      {/* Detail Side Panel */}
      {selectedMarker && markerDetails && (
        <div className="fixed right-0 top-0 h-full w-96 bg-[#18181b] border-l border-[#3f3f46] shadow-xl z-50 overflow-y-auto">
          <div className="p-4 border-b border-[#3f3f46] flex items-center justify-between">
            <h3 className="font-rajdhani font-bold">
              {selectedMarker.type === 'incident' ? 'Incident Details' : 'Officer Details'}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedMarker(null);
                setMarkerDetails(null);
              }}
            >
              ✕
            </Button>
          </div>
          <div className="p-4">
            {selectedMarker.type === 'incident' && markerDetails.incident && (
              <div className="space-y-4">
                <div>
                  <Badge
                    className="text-sm"
                    style={{ backgroundColor: `${SEVERITY_COLORS[markerDetails.incident.severity]}20`, color: SEVERITY_COLORS[markerDetails.incident.severity] }}
                  >
                    {markerDetails.incident.severity?.toUpperCase()}
                  </Badge>
                  <div className="text-xl font-bold mt-2">{markerDetails.incident.ir_number}</div>
                  <div className="text-zinc-400">{markerDetails.incident.title || markerDetails.incident.type}</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Site</span>
                    <span>{markerDetails.incident.site_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Status</span>
                    <Badge className="bg-zinc-700">{markerDetails.incident.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Reported</span>
                    <span>{formatTimeAgo(markerDetails.incident.created_at)}</span>
                  </div>
                  {markerDetails.assigned_officer && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Assigned</span>
                      <span>{markerDetails.assigned_officer.name}</span>
                    </div>
                  )}
                </div>
                {markerDetails.incident.description && (
                  <div className="mt-4 p-3 bg-[#09090b] rounded text-sm">
                    {markerDetails.incident.description}
                  </div>
                )}
              </div>
            )}
            
            {selectedMarker.type === 'officer' && markerDetails.user && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <OfficerAvatar
                    user={markerDetails.user}
                    size="executive"
                    showRing={false}
                    className="ring-2 ring-emerald-500/70 shadow-lg shadow-emerald-950/30"
                  />
                  <div>
                    <div className="font-bold text-base text-white truncate">
                      {markerDetails.user.name}
                    </div>
                    <div className="text-sm text-zinc-400">
                      {markerDetails.user.role}
                    </div>
                    {(markerDetails.user.call_sign || markerDetails.user.unit_id) && (
                      <div className="text-xs text-emerald-400 mt-0.5">
                        {markerDetails.user.call_sign
                          ? `Call sign: ${markerDetails.user.call_sign}`
                          : `Unit: ${markerDetails.user.unit_id}`}
                      </div>
                    )}
                  </div>
                </div>
                {markerDetails.attendance && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Site</span>
                      <span>{markerDetails.attendance.site_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Clock In</span>
                      <span>{formatTime(markerDetails.attendance.clock_in_time)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">GPS Verified</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    </div>
                  </div>
                )}
                {markerDetails.flags?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-bold text-yellow-400 mb-2">Flags</div>
                    {markerDetails.flags.map((flag, idx) => (
                      <div key={idx} className="p-2 bg-yellow-500/10 rounded text-xs text-yellow-400 mb-1">
                        {flag.description}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Tracking Section */}
                <div className="border-t border-[#3f3f46] pt-4 mt-4">
                  <OfficerTrackingDetails
                    officer={{
                      ...markerDetails.user,
                      id: selectedMarker.id,
                      site_name: markerDetails.attendance?.site_name,
                    }}
                    trackingData={selectedOfficerTracking}
                    siteVisits={siteVisits}
                    onViewTrail={() => {
                      const mapEl = document.getElementById('command-centre-map');
                      if (mapEl) {
                        mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }

                      toast.success('Breadcrumb trail loaded on Command Centre map.');
                    }}
                    isLoading={trackingLoading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

      {/* Clear Incidents Dialog */}
      <Dialog open={showClearIncidentsDialog} onOpenChange={setShowClearIncidentsDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Trash2 className="w-5 h-5" />
              Clear All Incidents
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will archive all incidents from the live feed. Archived incidents can be recovered from the Feed Management page.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46]">
              <p className="text-zinc-300">
                You are about to archive <span className="font-bold text-white">{incidentsFeed.length}</span> incidents from the live feed.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowClearIncidentsDialog(false)}
              className="border-zinc-600"
              disabled={clearingIncidents}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleClearAllIncidents}
              className="bg-red-600 hover:bg-red-700"
              disabled={clearingIncidents}
              data-testid="confirm-clear-incidents-btn"
            >
              {clearingIncidents ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Attendance Dialog */}
      <Dialog open={showClearAttendanceDialog} onOpenChange={setShowClearAttendanceDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Trash2 className="w-5 h-5" />
              Clear Attendance Records
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will archive all attendance records from the activity feed. Archived records can be recovered from the Feed Management page.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46]">
              <p className="text-zinc-300">
                You are about to archive <span className="font-bold text-white">{attendanceFeed.length}</span> attendance records.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowClearAttendanceDialog(false)}
              className="border-zinc-600"
              disabled={clearingAttendance}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleClearAllAttendance}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={clearingAttendance}
              data-testid="confirm-clear-attendance-btn"
            >
              {clearingAttendance ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KpiDrilldownSheet
        open={drilldown.open}
        onClose={() => setDrilldown({ open: false, kpiType: null, title: null, value: null })}
        kpiType={drilldown.kpiType}
        title={drilldown.title}
        kpiValue={drilldown.value}
      />
    </>
  );
}

// Status Card Component
function StatusCard({ icon, title, value, badge, badgeColor, subtitle, color = 'blue', trend, onClick, kpiType }) {
  const colorClasses = {
    blue: 'text-blue-400',
    emerald: 'text-emerald-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400',
    zinc: 'text-zinc-400',
  };

  const badgeClasses = {
    red: 'bg-red-500/20 text-red-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
  };

  const isClickable = !!onClick;

  return (
    <Card
      className={`bg-[#18181b] border-[#3f3f46] transition-all ${isClickable ? 'cursor-pointer hover:border-zinc-500 hover:bg-[#1f1f23] hover:shadow-lg hover:shadow-black/20 active:scale-[0.98]' : ''}`}
      onClick={onClick}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `View ${title} details` : undefined}
      data-testid={kpiType ? `kpi-card-${kpiType}` : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`${colorClasses[color]}`}>{icon}</div>
          <div className="flex items-center gap-1.5">
            {badge && (
              <Badge className={`text-[10px] ${badgeClasses[badgeColor]}`}>
                {badge}
              </Badge>
            )}
            {isClickable && (
              <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
            )}
          </div>
        </div>
        <div className={`text-3xl font-rajdhani font-bold mt-2 ${colorClasses[color]}`}>
          {value}
        </div>
        <div className="text-xs text-zinc-500 mt-1">{title}</div>
        {subtitle && <div className="text-[10px] text-zinc-600">{subtitle}</div>}
        {isClickable && <div className="text-[9px] text-zinc-700 mt-0.5">Click to view records</div>}
      </CardContent>
    </Card>
  );
}
